import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Container, Section } from '@/components/ui';

interface PageHeroProps {
  /** Small pill above the heading. Omitted entirely when absent. */
  eyebrow?: string;
  title: string;
  /**
   * A trailing slice of `title` to set in the brand gradient.
   *
   * Passed as text rather than as markup so the heading stays a single string for SEO
   * and for anything reading `document.title` alongside it. If it is not actually the
   * end of the title the whole heading renders plain — a silent no-op rather than a
   * mangled heading, because a typo here should not corrupt the page's h1.
   */
  highlight?: string;
  description?: string;
  /** One extra line under the description — "Ideal for: …" and the like. */
  note?: string;
  /** Buttons. Rendered in a row that wraps, centred with everything else. */
  actions?: ReactNode;
  /** Anything that belongs above the eyebrow, such as a back link. */
  above?: ReactNode;
  /**
   * An illustration above everything else.
   *
   * Separate from `above` because it gets its own width and spacing — an image sized to
   * the text column looks like a mistake, and one sized to the page pushes the heading
   * below the fold.
   */
  image?: ReactNode;
  className?: string;
}

/**
 * The heading band at the top of an inner page.
 *
 * Centred, with a pill eyebrow and the tail of the heading in the brand gradient —
 * the same treatment as the homepage hero, so a service page does not read as a
 * different site. It replaces a left-aligned `SectionHeading`, which was accurate but
 * plain: black text and a paragraph, with nothing marking where the page begins.
 *
 * The band's padding comes from `size="hero"`, so the space under the fixed nav and the
 * gap down to the first content section stay consistent with every other page.
 */
export function PageHero({
  eyebrow,
  title,
  highlight,
  description,
  note,
  actions,
  above,
  image,
  className,
}: PageHeroProps): ReactNode {
  const splits =
    highlight !== undefined && highlight !== '' && title.endsWith(highlight)
      ? { lead: title.slice(0, title.length - highlight.length), tail: highlight }
      : { lead: title, tail: '' };

  return (
    <Section tone="tint" size="hero" className={className}>
      <Container>
        <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
          {/*
            Narrower and tighter than it looks like it should be. The artwork carries a
            wide margin of its own, so `mb-8` on top of that opened a gap the eye reads
            as a mistake — and at 520px the illustration pushed the form itself below the
            fold on a laptop, which is the one thing a contact page must not do.
          */}
          {image ? <div className="mb-2 w-full max-w-[400px]">{image}</div> : null}

          {above ? <div className="mb-6">{above}</div> : null}

          {eyebrow ? (
            <span className="mb-6 rounded-full bg-primary/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-primary">
              {eyebrow}
            </span>
          ) : null}

          <h1 className="text-[2rem] font-bold leading-[1.12] tracking-tight text-heading md:text-[3rem]">
            {splits.lead}
            {splits.tail === '' ? null : (
              <span className="text-gradient bg-gradient-to-r from-primary-strong to-primary">
                {splits.tail}
              </span>
            )}
          </h1>

          {description ? (
            <p className="mt-5 max-w-2xl text-[1.0625rem] leading-relaxed text-body">
              {description}
            </p>
          ) : null}

          {note ? <p className="mt-4 text-sm text-muted">{note}</p> : null}

          {actions ? (
            <div
              className={cn(
                'mt-8 flex w-full flex-col items-stretch gap-3',
                'sm:w-auto sm:flex-row sm:items-center sm:justify-center',
              )}
            >
              {actions}
            </div>
          ) : null}
        </div>
      </Container>
    </Section>
  );
}
